import { unref } from 'vue'
import { useAuthStore } from '@/store/pinia/auth'
import { useDevStore } from '@/store/pinia/dev'
import { EMPTY_ORG_NAMING_RULES, useOrganizationStore } from '@/store/pinia/organization'
import { useRootStore } from '@/store/pinia/root'
import { objectToListOfPairs, convertOwnerToUsername } from '@/utils/api/client'
import { checkUserRoutePermissions } from '@/utils/plugins/permissions'
// oxlint-disable-next-line no-unused-vars
import { pinia } from '../plugins/pinia'

/**
  * All API operations should be named the same as outlined within the BE's swagger spec.
  * @see https://docs.staging.cycloid.io/api/index.html
  * @see https://github.com/cycloidio/youdeploy-http-api/blob/develop/swagger.yml
 */

// API Error codes present in the error details payload contained in the error payload.
// @see Cycloid API conventions in the Cycloid wiki and YD API spec.
export const ERROR_CODE_ALREADY_EXISTS = 'already_exists'
export const ERROR_CODE_INVALID = 'invalid'
export const ERROR_CODE_INVALID_FIELD = 'invalid_field'
export const ERROR_CODE_UNAUTHENTICATED = 'unauthenticated'
export const ERROR_CODE_REFRESH_TOKEN = 'UnauthorizedRefreshToken'
export const DELETE = 'DELETE'
export const GET = 'GET'
export const POST = 'POST'
export const PUT = 'PUT'

/** ALPHA endpoints: usually return mock data
  *
  * ! Once the BE endpoint is available:
  * - rename and move each ALPHA endpoint
  * - rework the syntax to match already implemented endpoints
  * - correct the doc link
  * - delete the associated mock data files
  */
// oxlint-disable-next-line no-unused-vars
const ALPHA = {}

/** https://docs.staging.cycloid.io/api/index.html#operation/abortBuild */
const abortBuild = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineName, jobName, buildId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineName}/jobs/${jobName}/builds/${buildId}/abort`,
    null,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/activateLicence */
const activateLicence = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/licence`,
    { body },
    statusFromHandlers(handlers, 422, 500),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/assignMemberToTeam */
const assignMemberToTeam = (...params) => async (orgCanonical, teamCanonical, username) => {
  const [authRequest, ...handlers] = params
  const body = { username }
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/teams/${teamCanonical}/members`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/canDo */
const canDo = (...params) => async (orgCanonical, { key: action }, canonical) => {
  const [authRequest, ...handlers] = params
  const canonicals = _.isArray(canonical) ? canonical : [canonical]
  const body = {
    action,
    entity_canonicals: _.filter(canonicals),
  }

  const devStore = useDevStore(pinia)

  if (devStore.showDevThings) devStore.ADD_PERMISSION_FETCHED({ action, canonical })
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/can_do`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** Batch variant of canDo to reduce permission request fan-out */
const canDoBatch = (...params) => async (orgCanonical, actions = []) => {
  const [authRequest, ...handlers] = params
  const body = {
    actions: actions.map(({ canonical, action, entity_canonicals: canonicals = [], entityCanonicals = [] }) => ({
      canonical,
      action,
      entity_canonicals: _.filter(_.isArray(canonicals) ? canonicals : entityCanonicals),
    })),
  }
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/can_do_batch`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/checkResource */
const checkResource = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineName, resourceName) => {
  const [authRequest, ...handlers] = params
  const body = {}
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineName}/resources/${resourceName}/check`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/clearTaskCache */
const clearTaskCache = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineCanonical, jobCanonical, taskCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineCanonical}/jobs/${jobCanonical}/tasks/${taskCanonical}/cache`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/createAppearance */
const createAppearance = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/appearances`,
    { body },
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/costEstimateForms */
const costEstimateForms = (...params) => async (orgCanonical, projectCanonical, envCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/forms/estimate`,
    { body },
    statusFromHandlers(handlers, 404, 422, 500),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/pauseCloudAccountFinops */
const pauseCloudAccountFinops = (...params) => async (orgCanonical, cloudAccountCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/cloud_accounts/${cloudAccountCanonical}/finops/pause`,
    null,
    statusFromHandlers(handlers, 403, 404),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/unpauseCloudAccountFinops */
const unpauseCloudAccountFinops = (...params) => async (orgCanonical, cloudAccountCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/cloud_accounts/${cloudAccountCanonical}/finops/unpause`,
    null,
    statusFromHandlers(handlers, 403, 404),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/ingestCloudAccountFinops */
const ingestCloudAccountFinops = (...params) => async (orgCanonical, cloudAccountCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/cloud_accounts/${cloudAccountCanonical}/finops/ingest`,
    null,
    statusFromHandlers(handlers, 403, 404, 409),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getCloudCostManagementTagMapping */
const getCloudCostManagementTagMapping = (...params) => async (orgCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/cloud_cost_management/tag_mapping`,
    null,
    statusFromHandlers(handlers, 403, 404, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/putCloudCostManagementTagMapping */
const updateCloudCostManagementTagMapping = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/cloud_cost_management/tag_mapping`,
    { body },
    statusFromHandlers(handlers, 411, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getCloudCostManagementProvider */
const getCloudCostManagementProvider = (...params) => async (orgCanonical, providerCanonical, queryString) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/cloud_cost_management/${providerCanonical}${queryString}`,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getCloudCostManagementDashboard */
const getCloudCostManagementDashboard = (...params) => async (orgCanonical, queryString) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/cloud_cost_management${queryString}`,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getInventoryResources */
const getInventoryResources = (...params) => async (orgCanonical, pagination) => {
  const [authRequest, ...handlers] = params
  const filtersQuery = _.pickBy({ ...pagination }, (item) => !_.$isEmpty(item))
  const query = { ...filtersQuery, 'resources_provider[neq]': 'custom_resources' }
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/inventory`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/listAppearances */
const listAppearances = (...params) => async (orgCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/appearances`,
    null,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getResource */
const getResource = (...params) => async (orgCanonical, resourceId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/inventory/resources/${resourceId}`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/createAPIKey */
const createAPIKey = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  await convertOwnerToUsername(body, { isCreation: true })
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/api_keys`,
    { body },
    statusFromHandlers(handlers, 422, 404, 500, 409),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/createBuild */
const createBuild = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineName, jobName) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineName}/jobs/${jobName}/builds`,
    null,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/createConfigRepository */
const createConfigRepository = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/config_repositories`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/createPluginRegistry */
const createPluginRegistry = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/plugin_registries`,
    { body },
    statusFromHandlers(handlers, 411, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/createCredential */
const createCredential = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  await convertOwnerToUsername(body, { isCreation: true })
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/credentials`,
    { body },
    statusFromHandlers(handlers, 422, 500),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/createComponent */
const createEnvComponent = (...params) => async (orgCanonical, projectCanonical, environmentCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${environmentCanonical}/components`,
    { body },
    statusFromHandlers(handlers, 422, 500),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/createExternalBackend */
const createExternalBackend = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/external_backends`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/createFormsConfig */
const createFormsConfig = (...params) => async (orgCanonical, projectCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/forms/config`,
    { body },
    statusFromHandlers(handlers, 403, 404, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/validateFormsFile */
const validateFormsFile = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/forms/validate`,
    { body },
    statusFromHandlers(handlers, 403, 404, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/createInfraPolicy */
const createInfraPolicy = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  await convertOwnerToUsername(body, { isCreation: true })
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/infra_policies`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/createKpi */
const createKPI = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/projects/${body.project_canonical}/kpis`,
    { body },
    statusFromHandlers(handlers, 422, 404),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/createKPIFavorite */
const createKPIFavorite = (...params) => async (orgCanonical, projectCanonical, KPICanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/kpis/${KPICanonical}/favorites`,
    null,
    statusFromHandlers(handlers, 403, 404, 422, 500),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/createOAuthUser */
const createOAuthUser = (...params) => async (socialType, body) => {
  const [publicRequest] = params
  const res = await publicRequest(
    POST,
    `user/${socialType}/oauth`,
    { body },
    [401, 422],
  )
  return jsonBodyParser(res)
}

/** https://docs.staging.cycloid.io/api/index.html#operation/createOrg */
const createOrg = (...params) => async (body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/createOrgChild */
const createOrgChild = (...params) => async (body, parentCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${parentCanonical}/children`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/createOrgRole */
const createOrgRole = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/roles`,
    { body },
    statusFromHandlers(handlers, 422, 409),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/createPipeline */
const createPipeline = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/createProject */
const createProject = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  await convertOwnerToUsername(body, { isCreation: true })
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/projects`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/#tag/Organization-projects/operation/createEnvironment */
const createProjectEnvironment = (...params) => async (orgCanonical, projectCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments`,
    { body },
    statusFromHandlers(handlers, 422, 500),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** Link an existing organization environment to a project
 * POST /organizations/{organization_canonical}/projects/{project_canonical}/environments
 */
const linkEnvironmentToProject = (...params) => async (orgCanonical, projectCanonical, envCanonical) => {
  const [authRequest, ...handlers] = params
  const body = { environment_canonical: envCanonical }
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments`,
    { body },
    statusFromHandlers(handlers, 422, 500),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/createProjectFavorite */
const createProjectFavorite = (...params) => async (orgCanonical, projectCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/favorites`,
    null,
    statusFromHandlers(handlers, 403, 404, 422, 500),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/createServiceCatalog */
const createServiceCatalog = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/service_catalogs`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/createServiceCatalogFromTemplate */
const createServiceCatalogFromTemplate = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/service_catalogs/${body.template_ref}/template`,
    { body: _.omit(body, 'template_ref') },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/createServiceCatalogSource */
const createServiceCatalogSource = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  await convertOwnerToUsername(body, { isCreation: true })
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/service_catalog_sources`,
    { body },
    statusFromHandlers(handlers, 409, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/createSubscription */
const createSubscription = (...params) => async (orgCanonical, planCanonical) => {
  const [authRequest, ...handlers] = params
  const body = { plan_canonical: planCanonical }
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/subscriptions`,
    { body },
    statusFromHandlers(handlers, 404, 411, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/createTeam */
const createTeam = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  await convertOwnerToUsername(body, { isCreation: true })
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/teams`,
    { body },
    statusFromHandlers(handlers, 422),
  )

  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/#tag/Organization-components/operation/configureComponent */
const configureEnvComponent = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/stack_config`,
    { body },
    statusFromHandlers(handlers, 204, 403, 404, 422, 500),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deleteAPIKey */
const deleteAPIKey = (...params) => async (orgCanonical, apiKeyCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}/api_keys/${apiKeyCanonical}`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deleteAppearance */
const deleteAppearance = (...params) => async (orgCanonical, appearanceCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}/appearances/${appearanceCanonical}`,
    null,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deleteConfigRepository */
const deleteConfigRepository = (...params) => async (orgCanonical, configRepositoryCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}/config_repositories/${configRepositoryCanonical}`,
    null,
    statusFromHandlers(handlers, 409),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deletePluginRegistry */
const deletePluginRegistry = (...params) => async (orgCanonical, pluginRegistryId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}/plugin_registries/${pluginRegistryId}`,
    null,
    statusFromHandlers(handlers, 403, 404),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deleteCredential */
const deleteCredential = (...params) => async (orgCanonical, credentialCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}/credentials/${credentialCanonical}`,
    null,
    statusFromHandlers(handlers, 409),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deleteExternalBackend */
const deleteExternalBackend = (...params) => async (orgCanonical, externalBackendId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}/external_backends/${externalBackendId}`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deleteInfraPolicy */
const deleteInfraPolicy = (...params) => async (orgCanonical, infraPolicyCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}/infra_policies/${infraPolicyCanonical}`,
    null,
    statusFromHandlers(handlers, 409),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deleteInvitation */
const deleteInvitation = (...params) => async (orgCanonical, inviteId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}/members/${inviteId}`,
    null,
    statusFromHandlers(handlers, 403, 404),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deleteKpi */
const deleteKPI = (...params) => async (orgCanonical, projectCanonical, KPICanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/kpis/${KPICanonical}`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deleteKPIFavorite */
const deleteKPIFavorite = (...params) => async (orgCanonical, projectCanonical, KPICanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/kpis/${KPICanonical}/favorites`,
    null,
    statusFromHandlers(handlers, 403, 404, 422, 500),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deleteOrg */
const deleteOrg = (...params) => async (orgCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}`,
    null,
    statusFromHandlers(handlers, 422, 500),
  )
  const handledRes = await applyHandlers(res, handlers)
  const body = await jsonBodyParser(handledRes)
  // A 500 whose body doesn't carry a structured errors array (e.g. gateway HTML,
  // { message: "..." } shapes) must still signal failure so the store guard fires.
  if (handledRes?.status === 500 && !body?.errors) {
    return { errors: [{}] }
  }
  return body
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deleteOrgRole */
const deleteOrgRole = (...params) => async (orgCanonical, roleCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}/roles/${roleCanonical}`,
    null,
    statusFromHandlers(handlers, 404, 409, 500),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deletePipeline */
const deletePipeline = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineCanonical}`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deleteProject */
const deleteProject = (...params) => async (orgCanonical, projectCanonical, options = {}) => {
  const [authRequest, ...handlers] = params
  const query = {}
  if (options.ignore_config_files_err) query.ignore_config_files_err = true
  if (options.skip_hooks) query.skip_hooks = true
  const reqQuery = _.isEmpty(query) ? null : { query }
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}/projects/${projectCanonical}`,
    reqQuery,
    statusFromHandlers(handlers, 403),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deleteProjectEnvironment */
const deleteProjectEnvironment = (...params) => async (orgCanonical, projectCanonical, environmentCanonical, options = {}) => {
  const [authRequest, ...handlers] = params
  const query = {}
  if (options.ignore_config_files_err) query.ignore_config_files_err = true
  if (options.skip_hooks) query.skip_hooks = true
  const reqQuery = _.isEmpty(query) ? null : { query }
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${environmentCanonical}`,
    reqQuery,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deleteProjectFavorite */
const deleteProjectFavorite = (...params) => async (orgCanonical, projectCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/favorites`,
    null,
    statusFromHandlers(handlers, 403, 404, 422, 500),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deleteServiceCatalog */
const deleteServiceCatalog = (...params) => async (orgCanonical, stackRef) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}/service_catalogs/${stackRef}`,
    null,
    statusFromHandlers(handlers, 404, 422, 500),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deleteServiceCatalogSource */
const deleteServiceCatalogSource = (...params) => async (orgCanonical, serviceCatalogSourceCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}/service_catalog_sources/${serviceCatalogSourceCanonical}`,
    null,
    statusFromHandlers(handlers, 409),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deleteTeam */
const deleteTeam = (...params) => async (orgCanonical, teamCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}/teams/${teamCanonical}`,
    null,
    statusFromHandlers(handlers, 403, 404, 500),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deleteUserAccount */
const deleteUserAccount = (...params) => async () => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/user`,
    null,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(res)
}

/** https://docs.staging.cycloid.io/api/index.html#operation/diffPipeline */
const diffPipeline = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineCanonical}/diff`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/disableResourceVersion */
const disableResourceVersion = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineName, resourceName, versionId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineName}/resources/${resourceName}/versions/${versionId}/disable`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/emailVerification */
const emailVerification = (...params) => async (verificationToken) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/user/email/verification/${verificationToken}`,
    null,
    statusFromHandlers(handlers, 404, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/emailVerificationResend */
const emailVerificationResend = (...params) => async (body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/user/email/verification`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/enableResourceVersion */
const enableResourceVersion = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineName, resourceName, versionId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineName}/resources/${resourceName}/versions/${versionId}/enable`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getActiveAppearance */
const getActiveAppearance = (...params) => async (orgCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/appearance/${orgCanonical}`,
    null,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getAPIKey */
const getAPIKey = (...params) => async (orgCanonical, apiKeyCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/api_keys/${apiKeyCanonical}`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getAPIKeys */
const getAPIKeys = (...params) => async (orgCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/api_keys`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getConfig */
const getSentryEnable = (...params) => async () => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    '/config',
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getConfig */ // ! different from BE name: for clarity
const getAppConfig = (...params) => async () => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    '/authentications',
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

const getOrgConfig = (...params) => async (orgCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/authentications/${orgCanonical}`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getAppearance */
const getAppearance = (...params) => async (orgCanonical, appearanceCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/appearances/${appearanceCanonical}`,
    null,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getAppVersion */
const getAppVersion = (...params) => async () => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/version`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getBuild */
const getBuild = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineName, jobName, buildId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineName}/jobs/${jobName}/builds/${buildId}`,
    null,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getBuildPlan */
const getBuildPlan = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineCanonical, jobCanonical, buildId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineCanonical}/jobs/${jobCanonical}/builds/${buildId}/plan`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getBuildPreparation */
const getBuildPreparation = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineName, jobName, buildId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineName}/jobs/${jobName}/builds/${buildId}/preparation`,
    null,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getBuildResources */
const getBuildResources = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineCanonical, jobCanonical, buildId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineCanonical}/jobs/${jobCanonical}/builds/${buildId}/resources`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getBuilds */
const getBuilds = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineName, jobName) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineName}/jobs/${jobName}/builds`,
    null,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getBuildsWithVersionAsInput */
const getBuildsWithVersionAsInput = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineName, resourceName, versionId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineName}/resources/${resourceName}/versions/${versionId}/input_to`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getBuildsWithVersionAsOutput */
const getBuildsWithVersionAsOutput = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineName, resourceName, versionId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineName}/resources/${resourceName}/versions/${versionId}/output_of`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getCloudProviders */
const getCloudProviders = (...params) => async (orgCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/cloud_providers`,
    null,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getConfigRepositories */
const getConfigRepositories = (...params) => async (orgCanonical, pagination) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({ ...pagination }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/config_repositories`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 500),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getConfigRepository */
const getConfigRepository = (...params) => async (orgCanonical, configRepositoryCanonical, pagination) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({ ...pagination }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/config_repositories/${configRepositoryCanonical}`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getCountries */
const getCountries = (...params) => async () => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/countries`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getCredential */
const getCredential = (...params) => async (orgCanonical, credentialCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/credentials/${credentialCanonical}`,
    null,
    statusFromHandlers(handlers, 404),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/listCredentials */
const listCredentials = (...params) => async (orgCanonical, type, pagination) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({
    credential_type: type,
    ...pagination,
  }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/credentials`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 500),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getCredentialOptions */
const getCredentialOptions = (...params) => async (orgCanonical, credentialCanonical, service) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/credentials/${credentialCanonical}/options?service=${service}`,
    null,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getCredentialOptionsBare */
const getCredentialOptionsBare = (...params) => async (orgCanonical, raw, type, service) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/credentials/options?service=${service}`,
    { body: { raw, type } },
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** A custom call, created in order to fetch cycloid creds only:
 * - vault
 * - cycloid-worker-keys
 * */
const getCycloidCredential = (...params) => async (orgCanonical, credentialCanonical) => {
  const [authRequest, ...handlers] = params
  if (!['vault', 'cycloid-worker-keys'].includes(credentialCanonical)) return console.error('This endpoint is for Cycloid creds only. Please use "getCredential" instead')
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/credentials/${credentialCanonical}`,
    null,
    statusFromHandlers(handlers, 404),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getDefaultAppearance */
const getDefaultAppearance = (...params) => async () => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    '/appearance',
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getEnvironments */
const getEnvironments = (...params) => async (orgCanonical, pagination = {}) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({ ...pagination }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/environments`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getEnvironmentTypes */
const getEnvironmentTypes = (...params) => async (orgCanonical, pagination = {}) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({ ...pagination }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/environment_types`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/createEnvironmentType */
const createEnvironmentType = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/environment_types`,
    { body },
    statusFromHandlers(handlers, 422, 500),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updateEnvironmentType */
const updateEnvironmentType = (...params) => async (orgCanonical, canonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/environment_types/${canonical}`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deleteEnvironmentType */
const deleteEnvironmentType = (...params) => async (orgCanonical, canonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}/environment_types/${canonical}`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/setEnvironmentTypeLabelSelector */
const setEnvironmentTypeLabelSelector = (...params) => async (orgCanonical, canonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/environment_types/${canonical}/label_selector`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deleteEnvironmentTypeLabelSelector */
const deleteEnvironmentTypeLabelSelector = (...params) => async (orgCanonical, canonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}/environment_types/${canonical}/label_selector`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getComponents */
const getComponents = (...params) => async (orgCanonical, pagination = {}) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({ ...pagination }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/components`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getCloudAccounts */
const getCloudAccounts = (...params) => async (orgCanonical, config = {}) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/cloud_accounts`,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getCloudAccount */
const getCloudAccount = (...params) => async (orgCanonical, cloudAccountCanonical, config = {}) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/cloud_accounts/${cloudAccountCanonical}`,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/createCloudAccount */
const createCloudAccount = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/cloud_accounts/with_credentials`,
    { body },
    statusFromHandlers(handlers, 422, 500),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updateCloudAccount */
const updateCloudAccount = (...params) => async (orgCanonical, cloudAccountCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/cloud_accounts/${cloudAccountCanonical}`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

const updateCloudAccountFinops = (...params) => async (orgCanonical, cloudAccountCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/cloud_accounts/${cloudAccountCanonical}/finops`,
    { body },
    statusFromHandlers(handlers, 422, 500),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deleteCloudAccount */
const deleteCloudAccount = (...params) => async (orgCanonical, cloudAccountCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}/cloud_accounts/${cloudAccountCanonical}`,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** Returns mock environment for now - API endpoint not yet available */
/** https://docs.staging.cycloid.io/api/index.html#operation/getOrganizationEnvironment */
const getOrganizationEnvironment = (...params) => async (orgCanonical, envCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/environments/${envCanonical}`,
    {},
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getOrganizationNamingRules */
const getOrganizationNamingRules = (...params) => async (orgCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/naming_rules`,
    null,
    statusFromHandlers(handlers, 404),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updateOrganizationNamingRules */
const updateOrganizationNamingRules = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/naming_rules`,
    { body: { ...EMPTY_ORG_NAMING_RULES, ...body } },
    statusFromHandlers(handlers, 411, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deleteOrganizationNamingRules */
const deleteOrganizationNamingRules = (...params) => async (orgCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}/naming_rules`,
    null,
    statusFromHandlers(handlers, 404),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/createOrganizationEnvironment */
const createOrganizationEnvironment = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/environments`,
    { body },
    statusFromHandlers(handlers, 422, 500),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updateOrganizationEnvironment */
const updateOrganizationEnvironment = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/environments/${body.canonical}`,
    { body: { ...body, owner: body.owner?.username ?? body.owner } },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deleteOrganizationEnvironment */
const deleteOrganizationEnvironment = (...params) => async (orgCanonical, environmentCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}/environments/${environmentCanonical}`,
    {},
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** Returns mock environment components for now - API endpoint not yet available */
/** https://docs.staging.cycloid.io/api/index.html#operation/getEnvironmentComponents */
const getEnvironmentComponents = (...params) => async (orgCanonical, projectCanonical, environmentCanonical, pagination = {}) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({ ...pagination }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${environmentCanonical}/components`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 422, 404),
  )
  const handled = await applyHandlers(res, handlers)
  const body = await jsonBodyParser(handled)
  // Flag the denial rather than leaving the caller to recognise it from an error code: the
  // component list permission is its own permission now (BE-473), so a caller shows a notice in
  // place of the list instead of treating it as a failed fetch. A 404 left unhandled (the
  // no-redirect variant, used for search/pagination refetches) is surfaced the same way, so a
  // miss on a later request shows the notice rather than bouncing the user off the page; the
  // initial load keeps handle404NotFound, which redirects before this line is reached.
  return [403, 404].includes(handled?.status) ? { ...body, forbidden: true } : body
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getComponent */
const getComponent = (...params) => async (orgCanonical, projectCanonical, environmentCanonical, componentCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${environmentCanonical}/components/${componentCanonical}`,
    null,
    // Accept 404/422/500 so a failed read resolves to a non-ok response instead of
    // throwing/redirecting — this is used for a best-effort background poll that
    // must never navigate away or pop a global alert (registered with NO_ERROR_HANDLING).
    statusFromHandlers(handlers, 404, 422, 500),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getExternalBackend */
const getExternalBackend = (...params) => async (orgCanonical, externalBackendId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/external_backends/${externalBackendId}`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getExternalBackends */
const getExternalBackends = (...params) => async (orgCanonical, query) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/external_backends`,
    { query },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getFormsValuesRef */
const getFormsValuesRef = (...params) => async (orgCanonical, url, transform, config) => {
  const [authRequest, ...handlers] = params
  const body = { url, transform, config }
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/forms/values_ref`,
    { body },
    statusFromHandlers(handlers, 404),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getInfraPolicies */
const getInfraPolicies = (...params) => async (orgCanonical, query) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/infra_policies`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getInfraPolicy */
const getInfraPolicy = (...params) => async (orgCanonical, infraPolicyCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/infra_policies/${infraPolicyCanonical}`,
    null,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getInvitations */
const getInvitations = (...params) => async (orgCanonical, query) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/members`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getJob */
const getJob = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineCanonical, jobCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineCanonical}/jobs/${jobCanonical}`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getJobs */
const getJobs = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineCanonical, pagination) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({ ...pagination }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineCanonical}/jobs`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getKpi */
const getKPI = (...params) => async (orgCanonical, KPICanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/kpis/${KPICanonical}`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getKpis */
const getKPIs = (...params) => async (
  orgCanonical,
  projectCanonical,
  begin,
  end,
  favorite,
  operator = 'eq',
) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({
    project_canonical: projectCanonical,
    begin,
    end,
    [`favorite[${operator}]`]: favorite,
  }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/kpis`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getLicence */
const getLicence = (...params) => async (orgCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/licence`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getOAuthUser */
const getOAuthUser = (...params) => async (socialType, query) => {
  const [publicRequest] = params
  const res = await publicRequest(
    GET,
    `user/${socialType}/oauth`,
    { query },
    [401, 422],
  )
  return jsonBodyParser(res)
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getOrg */
const getOrg = (...params) => async (orgCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getOrgAncestors */
const getOrgAncestors = (...params) => async (orgCanonical, pagination) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({ ...pagination }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/ancestors`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getOrgChildren */
const getOrgChildren = (...params) => async (orgCanonical, pagination, { token } = {}) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({ ...pagination }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/children`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 422),
    { token },
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getOrgEvents */
const getOrgEvents = (...params) => async (orgCanonical, query) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/events`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getOrgEventsTags */
const getOrgEventsTags = (...params) => async (orgCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/events/tags`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getOrgMember */
const getOrgMember = (...params) => async (orgCanonical, username) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/members/${username}`,
    null,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getOrgMembers */
const getOrgMembers = (...params) => async (orgCanonical, pagination) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({ ...pagination }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/members`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getOrgRole */
const getOrgRole = (...params) => async (orgCanonical, roleCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET, `/organizations/${orgCanonical}/roles/${roleCanonical}`,
    null,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getOrgRoles */
const getOrgRoles = (...params) => async (orgCanonical, pagination) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({ ...pagination }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/roles`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getOrgRunningInfraAWS */
const getOrgRunningInfraAWS = (...params) => async (orgCanonical, credentialCanonical, projectCanonical, resource, tag) => {
  const [authRequest, ...handlers] = params
  const query = objectToListOfPairs(_.pickBy({
    credential_canonical: credentialCanonical,
    project: projectCanonical,
    resource,
    tag,
  }, (item) => !_.$isEmpty(item)))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/infrastructures/aws`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getOrgRunningInfraAWSInstances */
const getOrgRunningInfraAWSInstances = (...params) => async (orgCanonical, credentialCanonical, projectCanonical, tag) => {
  const [authRequest, ...handlers] = params
  const query = objectToListOfPairs(_.pickBy({
    credential_canonical: credentialCanonical,
    project: projectCanonical,
    tag,
  }, (item) => !_.$isEmpty(item)))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/infrastructures/aws/instances`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getOrgSummary */
const getOrgSummary = (...params) => async (orgCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/summary`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

const listAuthentications = (...params) => async (orgCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/authentications`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

const updateAuthentication = (...params) => async (orgCanonical, authenticationType, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/authentications/${authenticationType}`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

const getAuthentication = (...params) => async (orgCanonical, authenticationType) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `organizations/${orgCanonical}/authentications/${authenticationType}`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/listOIDCGroupMappings */
const listOIDCGroupMappings = (...params) => async (orgCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/oidc-group-mappings`,
    null,
    statusFromHandlers(handlers, 403, 404),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/createOIDCGroupMapping */
const createOIDCGroupMapping = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/oidc-group-mappings`,
    { body },
    statusFromHandlers(handlers, 403, 404, 411, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deleteOIDCGroupMapping */
const deleteOIDCGroupMapping = (...params) => async (orgCanonical, oidcGroupMappingId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}/oidc-group-mappings/${oidcGroupMappingId}`,
    null,
    statusFromHandlers(handlers, 403, 404),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getOIDCOrganizationSettings */
const getOIDCOrganizationSettings = (...params) => async (orgCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/oidc-organization-settings`,
    null,
    statusFromHandlers(handlers, 403, 404),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updateOIDCOrganizationSettings
 * Rejects oidc_no_match_policy='eject' when oidc_managed is false with HTTP 422. */
const updateOIDCOrganizationSettings = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/oidc-organization-settings`,
    { body },
    statusFromHandlers(handlers, 403, 404, 411, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getOrgs */
const getOrgs = (...params) => async (pagination) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({ ...pagination }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    '/organizations',
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getPendingInvitation */
const getPendingInvitation = (...params) => async (verificationToken) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/members/verify/${verificationToken}`,
    null,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getPipeline */
const getPipeline = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineCanonical}`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

// Like getPipeline but 404-tolerant — used to detect legacy two-part pipeline canonicals.
const getPipelineSilent = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineCanonical}`,
    null,
    statusFromHandlers(handlers, 404, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getPipelineConfig */
const getPipelineConfig = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineCanonical}/config`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getResource */ // ! different from BE name: for clarity
const getPipelineResource = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineName, resourceName) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineName}/resources/${resourceName}`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getPipelineResources */
const getPipelineResources = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineCanonical, pagination) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({ ...pagination }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineCanonical}/resources`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getPipelineVariables */
const getPipelineVariables = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineCanonical}/variables`,
    null,
    statusFromHandlers(handlers, 404, 409, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getPipelines */
const getPipelines = (...params) => async (orgCanonical, pagination) => {
  const [authRequest, ...handlers] = params
  const query = objectToListOfPairs(_.pickBy({ ...pagination }, (item) => !_.$isEmpty(item)))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/pipelines`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getPluginRegistries */
const getPluginRegistries = (...params) => async (orgCanonical, pagination) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({ ...pagination }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/plugin_registries`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 403, 404, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/createPluginManager */
const createPluginManager = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/plugin_managers`,
    { body },
    statusFromHandlers(handlers, 404, 411, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/listPluginManagers */
const listPluginManagers = (...params) => async (orgCanonical, pagination) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({ ...pagination }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/plugin_managers`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 403, 404, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getPluginManager */
const getPluginManager = (...params) => async (orgCanonical, pluginManagerId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/plugin_managers/${pluginManagerId}`,
    null,
    statusFromHandlers(handlers, 403, 404, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deletePluginManager */
const deletePluginManager = (...params) => async (orgCanonical, pluginManagerId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}/plugin_managers/${pluginManagerId}`,
    null,
    statusFromHandlers(handlers, 403, 404),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updatePluginManager */
const updatePluginManager = (...params) => async (orgCanonical, pluginManagerId, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/plugin_managers/${pluginManagerId}`,
    { body },
    statusFromHandlers(handlers, 403, 404, 411, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getPolicies */
const getPolicies = (...params) => async (orgCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/policies`,
    null,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getProjectEnvironments */
const getProjectEnvironments = (...params) => async (orgCanonical, projectCanonical, queryParams = {}) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({ ...queryParams }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getProjEnvLogEntries */
const getProjEnvLogEntries = (...params) => async ({ orgCanonical, projectCanonical, envCanonical, source, begin, end, query: theQuery }) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({
    query: theQuery,
    begin,
    end,
  }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/logs/sources/${source}`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 422, 500),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getProjEnvLogSources */
const getProjEnvLogSources = (...params) => async (orgCanonical, projectCanonical, envCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/logs/sources`,
    null,
    statusFromHandlers(handlers, 404),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getProjInfrastructure */
const getProjInfrastructure = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/infrastructure`,
    null,
    statusFromHandlers(handlers, 404, 422, 500),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getProject */
const getProject = (...params) => async (orgCanonical, projectCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}`,
    null,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getProjectConfig */ // ! different from BE name: for clarity
const getProjectEnvironmentConfig = (...params) => async (orgCanonical, projectCanonical, envCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environment/${envCanonical}/config`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getKpis */
const getProjectKPIs = (...params) => async (
  orgCanonical,
  projectCanonical,
  envCanonical,
  componentCanonical,
  begin,
  end,
  favorite,
  operator = 'eq',
) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({
    environment_canonical: envCanonical,
    component_canonical: componentCanonical,
    begin,
    end,
    [`favorite[${operator}]`]: favorite,
  }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/kpis`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/syncedPipeline */
const syncedPipeline = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineCanonical}/synced`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getProjectPipelines */
const getProjectPipelines = (...params) => async (orgCanonical, projectCanonical, pagination) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({ ...pagination }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/pipelines`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getProjects */
const getProjects = (...params) => async (orgCanonical, { pagination, orderBy, configRepositoryId, catalogRepositoryId, credentialId, favorite, filters, operator = 'eq' } = {}) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({
    ...pagination,
    order_by: orderBy,
    [`project_config_repository_id[${operator}]`]: configRepositoryId,
    [`service_catalog_source_id[${operator}]`]: catalogRepositoryId,
    [`credential_id[${operator}]`]: credentialId,
    [`favorite[${operator}]`]: favorite,
    ...filters,
  }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getRepoBranches */
const getRepoBranches = (...params) => async (orgCanonical, credentialCanonical, gitUrl) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({
    credential_canonical: credentialCanonical,
    git_url: gitUrl,
  }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/branches`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 404, 422, 500),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getResourceVersions */
const getResourceVersions = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineName, resourceName) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineName}/resources/${resourceName}/versions`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getServiceCatalog */
const getServiceCatalog = (...params) => async (stackRef, orgCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/service_catalogs/${stackRef}`,
    null,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/listServiceCatalogSourceVersions */
const listCatalogRepositoryVersions = (...params) => async (orgCanonical, catalogRepositoryCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/service_catalog_sources/${catalogRepositoryCanonical}/versions`,
    null,
    statusFromHandlers(handlers, 403, 404, 411, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getStackVersionAdoption */
const getStackVersionAdoption = (...params) => async (orgCanonical, stackRef) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/service_catalogs/${stackRef}/usage`,
    null,
    statusFromHandlers(handlers, 422, 500),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.cycloid.io/api/#tag/Organization-components/operation/getComponentStackConfiguration */
const getComponentStackConfiguration = (...params) => async (
  orgCanonical,
  projectCanonical,
  envCanonical,
  componentCanonical,
  queryParams,
) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({ ...queryParams }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/stack_config`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 403, 404, 422, 500),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getServiceCatalogSource */
const getServiceCatalogSource = (...params) => async (orgCanonical, serviceCatalogSourceCanonical, pagination) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({ ...pagination }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/service_catalog_sources/${serviceCatalogSourceCanonical}`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getServiceCatalogSources */
const getServiceCatalogSources = (...params) => async (orgCanonical, pagination) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({ ...pagination }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/service_catalog_sources`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 500),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getServiceCatalogTerraform */
const getServiceCatalogTerraform = (...params) => async ({ orgCanonical, stackRef }) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/service_catalogs/${stackRef}/default/terraform`,
    null,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getServiceCatalogTerraformDiagram */
const getServiceCatalogTerraformDiagram = (...params) => async ({ orgCanonical, stackRef }) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/service_catalogs/${stackRef}/terraform/diagram`,
    null,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getServiceCatalogTemplateUseCases */
const getServiceCatalogTemplateUseCases = (...params) => async (orgCanonical, stackRef, queryParams) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({ ...queryParams }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/service_catalogs/${stackRef}/use_cases`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getServiceCatalogVersions */
const getServiceCatalogVersions = (...params) => async (orgCanonical, stackRef) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/service_catalogs/${stackRef}/versions`,
    null,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getServiceCatalogs */
const getServiceCatalogs = (...params) => async (
  orgCanonical,
  {
    catalogVisibility,
    catalogRepositoryCanonical,
    catalogAuthor,
    catalogMaintainer,
    catalogCloudProviders,
    pagination,
    isBlueprint,
    labelSelectorRequirements,
  } = {},
) => {
  const [authRequest, ...handlers] = params
  const labelFilters = (labelSelectorRequirements || []).reduce((acc, { key, operator, values }) => {
    if (key && values?.length) acc[`service_catalog_label.${key}[${operator}]`] = values.join(',')
    return acc
  }, {})
  const query = _.pickBy({
    organization_canonical: orgCanonical,
    'service_catalog_visibility[eq]': catalogVisibility,
    'service_catalog_blueprint[eq]': isBlueprint,
    'service_catalog_source_canonical[eq]': catalogRepositoryCanonical,
    'team_canonical[in]': catalogMaintainer,
    'service_catalog_author[in]': catalogAuthor,
    'service_catalog_cloud_providers[in]': catalogCloudProviders,
    ...labelFilters,
    ...pagination,
  }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/service_catalogs`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getStatus */
const getStatus = (...params) => async () => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/status`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getEventsActionsAndEntities */
const getEventsActionsAndEntities = (...params) => async () => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    '/events/actions',
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/listNotifications */
const listNotifications = (...params) => async (query, options = {}) => {
  const [authRequest, ...handlers] = params

  const res = await authRequest(
    GET,
    '/user/notifications',
    { query },
    statusFromHandlers(handlers, 422),
    options,
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deleteNotification */
const deleteNotification = (...params) => async (notificationId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/user/notifications/${notificationId}`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deleteAllNotifications */
const deleteAllNotifications = (...params) => async () => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    '/user/notifications',
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/markNotificationAsRead */
const markNotificationAsRead = (...params) => async (notificationId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/user/notifications/${notificationId}/read`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/markNotificationAsUnread */
const markNotificationAsUnread = (...params) => async (notificationId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/user/notifications/${notificationId}/unread`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/markAllNotificationAsRead */
const markAllNotificationsAsRead = (...params) => async () => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    '/user/notifications/read_all',
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/listWatchRules */
const listWatchRules = (...params) => async (query) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/user/watch_rules`,
    { query },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getWatchRule */
const getWatchRule = (...params) => async (watchRuleCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/user/watch_rules/${watchRuleCanonical}`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updateWatchRule */
const updateWatchRule = (...params) => async (watchRuleCanonical, data) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/user/watch_rules/${watchRuleCanonical}`,
    { body: data },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deleteWatchRule */
const deleteWatchRule = (...params) => async (watchRuleCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/user/watch_rules/${watchRuleCanonical}`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/createWatchRule */
const createWatchRule = (...params) => async (data) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    '/user/watch_rules',
    { body: data },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getTeam */
const getTeam = (...params) => async (orgCanonical, teamCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/teams/${teamCanonical}`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getTeamMember */
const getTeamMember = (...params) => async (orgCanonical, teamCanonical, id) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/teams/${teamCanonical}/members/${id}`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getTeamMembers */
const getTeamMembers = (...params) => async (orgCanonical, teamCanonical, pagination) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({ ...pagination }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/teams/${teamCanonical}/members`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getTeams */
const getTeams = (...params) => async (orgCanonical, pagination) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({ ...pagination }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/teams`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getTerraformProvider */
const getTerraformProvider = (...params) => async ({ orgCanonical, providerId }) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/code_generation/terraform/providers/${providerId}`,
    null,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getTerraformProviderDataSource */
const getTerraformProviderDataSource = (...params) => async ({ orgCanonical, providerId, dataSourceId }) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/code_generation/terraform/providers/${providerId}/data_sources/${dataSourceId}`,
    null,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getTerraformProviderDataSources */
const getTerraformProviderDataSources = (...params) => async ({ orgCanonical, providerId }, pagination) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({ ...pagination }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/code_generation/terraform/providers/${providerId}/data_sources`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getTerraformProviderResource */
const getTerraformProviderResource = (...params) => async ({ orgCanonical, providerId, resourceId }) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/code_generation/terraform/providers/${providerId}/resources/${resourceId}`,
    null,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getTerraformProviderResources */
const getTerraformProviderResources = (...params) => async ({ orgCanonical, providerId }, pagination) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({ ...pagination }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/code_generation/terraform/providers/${providerId}/resources`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getTerraformProviders */
const getTerraformProviders = (...params) => async ({ orgCanonical }, pagination) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({ ...pagination }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/code_generation/terraform/providers`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getUserAccount */
const getUserAccount = (...params) => async () => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    '/user',
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getWorkers */
const getWorkers = (...params) => async (orgCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/workers`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/inviteUserToOrgMember */
const inviteUserToOrgMember = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/members`,
    { body },
    statusFromHandlers(handlers, 422, 404, 500),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/login */
const login = (...params) => async ({ email, password, orgCanonical }) => {
  const [publicRequest] = params
  const body = { email, password }
  if (orgCanonical) body.organization_canonical = orgCanonical
  const res = await publicRequest(
    POST,
    '/user/login',
    { body },
    [401, 403, 422],
  )
  return jsonBodyParser(res)
}

/** https://docs.staging.cycloid.io/api/index.html#operation/emailAuthenticationVerification */
const emailAuthenticationVerification = (...params) => async (token, orgCanonical) => {
  const [publicRequest] = params
  const query = orgCanonical ? { organization_canonical: orgCanonical } : null
  const res = await publicRequest(
    PUT,
    `/user/email/authentication/${token}`,
    query,
    [401, 422],
  )
  return jsonBodyParser(res)
}

/** https://docs.staging.cycloid.io/api/index.html#operation/passwordResetReq */
const passwordResetReq = (...params) => async (email) => {
  const [publicRequest] = params
  const res = await publicRequest(
    POST,
    '/user/reset_password',
    { body: { email } },
    [422],
  )
  return jsonBodyParser(res)
}

/** https://docs.staging.cycloid.io/api/index.html#operation/passwordResetUpdate */
const passwordResetUpdate = (...params) => async (token, password) => {
  const [publicRequest, ...handlers] = params
  const body = { token, password }
  const res = await publicRequest(
    PUT,
    '/user/reset_password',
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(res)
}

/** https://docs.staging.cycloid.io/api/index.html#operation/pauseJob */
const pauseJob = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineName, jobCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineName}/jobs/${jobCanonical}/pause`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/pausePipeline */
const pausePipeline = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineName) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineName}/pause`,
    null,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/pinResourceVersion */
const pinResourceVersion = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineName, resourceName, versionId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineName}/resources/${resourceName}/versions/${versionId}/pin`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/refreshServiceCatalogSource */
const refreshServiceCatalogSource = (...params) => async (orgCanonical, serviceCatalogSourceCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/service_catalog_sources/${serviceCatalogSourceCanonical}/refresh`,
    // Wait for stack presence to be recomputed so a branch discovered by this refresh
    // is immediately visible, instead of only appearing after a second refresh (ENG-308).
    { query: { sync_presence: true } },
    // A 404 here means the pinned git branch was removed. Accept it (paired with the
    // NO_REDIRECT strategy) so it is returned as an inline error instead of bouncing
    // the whole page to the global not-found route.
    statusFromHandlers(handlers, 404, 409, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/refreshServiceCatalogSourceVersions */
const refreshServiceCatalogSourceVersions = (...params) => async (orgCanonical, serviceCatalogSourceCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/service_catalog_sources/${serviceCatalogSourceCanonical}/versions/refresh`,
    // See refreshServiceCatalogSource above (ENG-308).
    { query: { sync_presence: true } },
    statusFromHandlers(handlers, 403, 404, 411, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/refreshToken */
const refreshToken = (...params) => async (query) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    '/user/refresh_token',
    { query },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/removeOrgMember */
const removeOrgMember = (...params) => async (orgCanonical, id) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}/members/${id}`,
    null,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/rerunBuild */
const rerunBuild = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineName, jobName, buildId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineName}/jobs/${jobName}/builds/${buildId}`,
    null,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/resendInvitation */
const resendInvitation = (...params) => async (orgCanonical, inviteId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/members/${inviteId}/resend`,
    null,
    statusFromHandlers(handlers, 422, 404, 500),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/resourceSetPinComment */
const resourceSetPinComment = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineName, resourceName, comment) => {
  const [authRequest, ...handlers] = params
  const body = { pin_comment: comment }
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineName}/resources/${resourceName}/pin_comment`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/signUp */
const signUp = (...params) => async (body) => {
  const [publicRequest] = params
  const res = await publicRequest(
    POST,
    '/user',
    { body },
    [422],
  )
  return jsonBodyParser(res)
}

/** https://docs.staging.cycloid.io/api/index.html#operation/signUpAWSMarketplace */
const signUpAWSMarketplace = (...params) => async (body) => {
  const [publicRequest] = params
  const res = await publicRequest(
    POST,
    '/user/aws_marketplace',
    { body },
    [422],
  )
  return jsonBodyParser(res)
}

/** https://docs.staging.cycloid.io/api/index.html#operation/terraformJSONToHCL */
const terraformJSONToHCL = (...params) => async ({ orgCanonical, configObj }) => {
  const [authRequest, ...handlers] = params
  const body = { config: configObj }
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/code_generation/terraform/jsontohcl`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/terraformValidate */
const terraformValidate = (...params) => async (orgCanonical, config) => {
  const [authRequest, ...handlers] = params
  const body = { config }
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/code_generation/terraform/validate`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/unassignMemberFromTeam */
const unassignMemberFromTeam = (...params) => async (orgCanonical, teamCanonical, id) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}/teams/${teamCanonical}/members/${id}`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/unpauseJob */
const unpauseJob = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineName, jobCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineName}/jobs/${jobCanonical}/unpause`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/unpausePipeline */
const unpausePipeline = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineName) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineName}/unpause`,
    null,
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/unpinResource */
const unpinResource = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineName, resourceName) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineName}/resources/${resourceName}/unpin`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updateApiKey */
const updateAPIKey = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  await convertOwnerToUsername(body)
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/api_keys/${body.canonical}`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updateAppearance */
const updateAppearance = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/appearances/${body.canonical}`,
    { body },
    statusFromHandlers(handlers),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updateConfigRepository */
const updateConfigRepository = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/config_repositories/${body.canonical}`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updateCredential */
const updateCredential = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  await convertOwnerToUsername(body)
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/credentials/${body.canonical}`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.cycloid.io/api/#tag/Organization-components/operation/updateComponent */
const updateEnvComponent = (...params) => async (orgCanonical, projectCanonical, envCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${body.canonical}`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deleteEnvComponent
 * 204: component removed immediately. 200 + JSON `data`: removal delegated to CI (`data` is a job payload or `{ jobs: [...] }`).
 */
const deleteEnvComponent = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, options = {}) => {
  const [authRequest, ...handlers] = params
  const query = {}
  if (options.ignore_config_files_err) query.ignore_config_files_err = true
  if (options.skip_hooks) query.skip_hooks = true
  const reqQuery = _.isEmpty(query) ? null : { query }
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}`,
    reqQuery,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getComponentConfig */
const getComponentConfig = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, queryParams = {}) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/config`,
    _.$isEmpty(queryParams) ? null : { query: queryParams },
    statusFromHandlers(handlers, 403, 404, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updateExternalBackend */
const updateExternalBackend = (...params) => async (orgCanonical, externalBackendId, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/external_backends/${externalBackendId}`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updateInfraPolicy */
const updateInfraPolicy = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  await convertOwnerToUsername(body)
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/infra_policies/${body.canonical}`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updateKpi */
const updateKPI = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/projects/${body.project_canonical}/kpis/${body.canonical}`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updateOrg */
const updateOrg = (...params) => async (body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${body.canonical}`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updateOrgMember */
const updateOrgMember = (...params) => async (orgCanonical, username, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/members/${username}`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updateOrgRole */
const updateOrgRole = (...params) => async (orgCanonical, roleCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/roles/${roleCanonical}`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updatePipeline */
const updatePipeline = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pipelineCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/pipelines/${pipelineCanonical}`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updatePluginRegistry */
const updatePluginRegistry = (...params) => async (orgCanonical, pluginRegistryId, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/plugin_registries/${pluginRegistryId}`,
    { body },
    statusFromHandlers(handlers, 403, 404, 411, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updateProject */
const updateProject = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  await convertOwnerToUsername(body)
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/projects/${body.canonical}`,
    { body },
    statusFromHandlers(handlers, 409, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updateServiceCatalog */
const updateServiceCatalog = (...params) => async (orgCanonical, stackRef, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/service_catalogs/${stackRef}`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updateServiceCatalogSource */
const updateServiceCatalogSource = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  await convertOwnerToUsername(body)
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/service_catalog_sources/${body.canonical}`,
    { body },
    statusFromHandlers(handlers, 404, 409, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updateServiceCatalogTerraform */
const updateServiceCatalogTerraform = (...params) => async (orgCanonical, stackRef, useCaseCanonical, config) => {
  const [authRequest, ...handlers] = params
  const body = { config }
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/service_catalogs/${stackRef}/${useCaseCanonical}/terraform`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updateServiceCatalogTerraformDiagram */
const updateServiceCatalogTerraformDiagram = (...params) => async ({ orgCanonical, stackRef, diagram: body }) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/service_catalogs/${stackRef}/terraform/diagram`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updateOrgCreationNotAllowedMessage */
const updateOrganizationCreationNotAllowed = (...params) => async (orgCanonical, message) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/messages`,
    { body: { creation_restricted_message: message } },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getOrgCreationNotAllowedMessage */
const getOrganizationCreationNotAllowed = (...params) => async (orgCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/messages`,
    null,
    statusFromHandlers(handlers, 422, 404),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updateTeam */
const updateTeam = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  await convertOwnerToUsername(body)
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/teams/${body.canonical}`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updateUserAccount */
const updateUserAccount = (...params) => async (body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    '/user',
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updateUserGuide */
const updateUserGuide = (...params) => async (body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    '/user/guide',
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/validateServiceCatalogDependencies */
const validateServiceCatalogDependencies = (...params) => async (stackRef, orgCanonical, projectCanonical, environmentCanonical) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({
    project_canonical: projectCanonical,
    environment_canonical: environmentCanonical,
  }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/service_catalogs/${stackRef}/dependencies/validate`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/validateServiceCatalogSource */
const validateServiceCatalogSource = (...params) => async (orgCanonical, catalogRepositoryCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/service_catalog_sources/${catalogRepositoryCanonical}/validate`,
    null,
    statusFromHandlers(handlers, 409, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

const getNotificationsSettings = (...params) => async () => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/user/notification_settings`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

const updateNotificationsSettings = (...params) => async (body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/user/notification_settings`,
    { body },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html##operation/listInventoryOutputs */
const getTerraformOutputs = (...params) => async (orgCanonical, filters = {}) => {
  const [authRequest, ...handlers] = params
  const query = { ...filters }
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/inventory/outputs`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

const pinTerraformOutput = (...params) => async (orgCanonical, inventoryOutputId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/inventory/outputs/${inventoryOutputId}/pin`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

const unpinTerraformOutput = (...params) => async (orgCanonical, inventoryOutputId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/inventory/outputs/${inventoryOutputId}/unpin`,
    null,
    statusFromHandlers(handlers, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** Log mocked endpoint in dev module, and wait for pauseDuration
 *
 * @param {string}    endpointName
 * @param {object=}   options
 * @param {number=}   options.pauseDuration  _default: 1000_
 */
// oxlint-disable-next-line no-unused-vars
async function logMockedEndpoint (endpointName, { pauseDuration = 1000 } = {}) {
  const devStore = useDevStore(pinia)

  if (devStore.showDevThings) devStore.ADD_MOCKED_ENDPOINT(endpointName)
  await _.$pause(pauseDuration)
}

/** Wraps the youdeploy-api-http-client~createHTTPClient method to handle the auto refresh token
 * automatically when the API responds with such requirement.
 *
 * @param {Function}  authRequest           - The method to wrap authenticated API requests
 * @param {Handler}   handle401Unauthenticated - The 401 response handler
 *
 * @returns {Function}                      - A function which has the same signature as the youdeploy-api-http-client~HTTPClient method.
 */
function authReqWithAutoRefreshToken (authRequest, router) {
  return async function (method, path, bodyAndQuery = {}, acceptedStatuses = [], options = {}) {
    const rootStore = useRootStore(pinia)
    // Optional chaining: the component-facing instance is created without a
    // real router (see vue-cycloid-plugin.js) — no snapshot there, the 404
    // handler then keeps its pre-existing behaviour.
    const routePathAtRequest = unref(router?.currentRoute)?.path
    let res = await authRequest(method, path, bodyAndQuery, [403, ...acceptedStatuses], options)

    const body = await jsonBodyParser(res)
    // Don't auto-refresh a per-request token override (options.token): a no-arg REFRESH_TOKEN()
    // would refresh the session token, not the override, then retry stale (ENG-364).
    const needToRefreshToken = !options.token && body?.errors?.[0]?.code === 'UnauthorizedRefreshToken'
    const requireOrganizationMFA = (body?.errors?.[0]?.code === 'RequireOrganizationMFA') ? body.errors : null

    if (needToRefreshToken) {
      const authStore = useAuthStore(pinia)
      await authStore.REFRESH_TOKEN()
      res = await authRequest(method, path, bodyAndQuery, [403, ...acceptedStatuses], options)
    }

    if (requireOrganizationMFA) {
      console.info(requireOrganizationMFA)
      rootStore.SET_ERRORS({ key: 'requireOrganizationMFA', errors: requireOrganizationMFA })
      router.push({ name: 'userSecurity' }).catch(() => { /* silenced */ })
    }

    // Lets response handlers tell a response meant for the page the user is
    // looking at apart from a stale one that outlived a navigation.
    if (res) res.$routePathAtRequest = routePathAtRequest

    return res
  }
}

/** Create a handler for 500 status codes */
function makeHandler500UnexpectedError () {
  const handler = (response) => {
    if (response.status !== handler.status) return response

    return null
  }

  handler.status = 500
  return handler
}

/** Create a handler for 401 status codes */
function makeHandler401Unauthenticated (router) {
  const handler = (response) => {
    if (response.status !== handler.status) return response

    router.replace({ name: 'logout' }).catch(() => { /* silenced */ })
    return null
  }

  handler.status = 401
  return handler
}

/** Create a handler for 404 status codes, redirects to "notFound" */
function makeHandler404NotFound (router) {
  const handler = (response) => {
    if (response.status !== handler.status) return response

    const { path, meta = {} } = unref(router.currentRoute)

    // A 404 landing after the user already navigated away is a stale
    // background response, not a miss on the page they are looking at —
    // swallow it so it cannot hijack the new page.
    if (response.$routePathAtRequest && response.$routePathAtRequest !== path) return null

    const { backRouteTo } = _.cloneDeep(meta)
    router.replace({ name: 'notFound', state: { 0: path, backRouteTo } })
    return null
  }

  handler.status = 404
  return handler
}

/** Create a handler for 403 status codes, redirects to license page if the response body
 * has an "InvalidLicence" error code, otherwise to "forbidden" */
/** Bounces to the plan settings when a 403 is the licence rather than the permissions,
 * as that is what the user has to act on. Shared by both 403 handlers.
 *
 * @param {Response} response
 * @param {VueRouter} router
 * @returns {Promise<boolean>} whether the redirect was made, so the caller stops there
 */
async function redirectOnInvalidLicence (response, router) {
  try {
    const body = await response.clone().json()
    if (body?.errors?.[0]?.code === 'InvalidLicence') {
      const orgStore = useOrganizationStore(pinia)
      router.replace({ name: 'orgSettings', params: { orgCanonical: orgStore.orgCanonical }, query: { section: 'plan' } }).catch(() => { /* silenced */ })
      return true
    }
  } catch (_) {}

  return false
}

/** Returns a 403 to the caller instead of swallowing it, so that the denial can be shown
 * where it happened. Only the licence redirect is kept, everything else is the caller's to
 * handle - it is meant for the requests registered with the SHOW_FORBIDDEN params.
 *
 * @param {VueRouter} router
 */
function makeHandler403ShowForbidden (router) {
  /** @param {Response} response */
  const handler = async (response) => {
    if (response.status !== handler.status) return response
    if (await redirectOnInvalidLicence(response, router)) return null

    return response
  }

  handler.status = 403
  return handler
}

function makeHandler403Forbidden (router) {
  /** @param {Response} response */
  const handler = async (response) => {
    if (response.status !== handler.status) return response

    if (await redirectOnInvalidLicence(response, router)) return null

    // The component-facing instance has no router (`createYDAPI(client, i18n, {})` in
    // vue-cycloid-plugin.js), so there is no route to check nor to bounce from: swallow
    // the denial like any other background 403 instead of throwing (ENG-359).
    const route = unref(router?.currentRoute)
    if (!route) return null

    const { path, name, params, meta = {} } = route

    // Only bounce to /forbidden for a genuine route-level denial. A 403 from a
    // secondary/background fetch on a page the user is allowed to view is
    // swallowed so the page stays put (the unauthorized section just renders nothing).
    if (!checkUserRoutePermissions(router, { name, params })) {
      const { backRouteTo, permissionsKey } = _.cloneDeep(meta)
      router.replace({ name: 'forbidden', params: { permissionsKey }, state: { 0: path, backRouteTo } }).catch(() => { /* silenced */ })
    }
    return null
  }

  handler.status = 403
  return handler
}

function makeHandler409Conflict () {
  const handler = (response) => response

  handler.status = 409
  return handler
}

/** Get a list of all the statuses from treated handlers
 *
 * @param {Handler[]}   handlers  - List of handlers.
 * @param {Number[]}    [status]  - Additional status to add to the list.
 * @returns {Number[]}            - The list with all the statuses codes of the handlers,
 *                                  plus the ones provided by parameter.
 */
function statusFromHandlers (handlers, ...status) {
  return [...handlers.map((h) => h.status), ...status]
}

/** Helper function which executes a list of handlers stopping at the first one
 * It handles the response, otherwise it returns the response passed by parameter.
 *
 * @param {WebAPI~Response} res       - The response to pass to the handlers.
 * @param {Handler[]}       handlers  - List of handlers to execute in order.
 * @returns {?Response}               - The response passed by parameter if any handler treated it,
 *                                      otherwise null.
 */
async function applyHandlers (res, handlers) {
  for (const h of handlers) {
    if (res === null) {
      break
    }

    res = await h(res)
  }

  return res
}

async function jsonBodyParser (res) {
  let body = null
  try {
    body = await res?.clone()?.json()
  } catch (error) {}
  return body ?? null
}

/* Returns a snake_cased object for use with the api */
function getPagination (index, size) {
  return {
    page_index: index,
    page_size: size,
  }
}

export {
  getPagination as pagination,
}

/** https://docs.staging.cycloid.io/api/index.html#operation/createPluginRegistryPlugin */
const createPluginRegistryPlugin = (...params) => async (orgCanonical, pluginRegistryId, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/plugin_registries/${pluginRegistryId}/plugins`,
    { body },
    statusFromHandlers(handlers, 411, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getPluginRegistryPlugin */
const getPluginRegistryPlugin = (...params) => async (orgCanonical, pluginRegistryId, pluginId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/plugin_registries/${pluginRegistryId}/plugins/${pluginId}`,
    null,
    statusFromHandlers(handlers, 403, 404),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deletePluginRegistryPlugin */
const deletePluginRegistryPlugin = (...params) => async (orgCanonical, pluginRegistryId, pluginId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}/plugin_registries/${pluginRegistryId}/plugins/${pluginId}`,
    null,
    statusFromHandlers(handlers, 403, 404),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updatePluginRegistryPlugin */
const updatePluginRegistryPlugin = (...params) => async (orgCanonical, pluginRegistryId, pluginId, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/plugin_registries/${pluginRegistryId}/plugins/${pluginId}`,
    { body },
    statusFromHandlers(handlers, 403, 404, 411, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/listPlugins */
const listPlugins = (...params) => async (orgCanonical, pagination) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({ ...pagination }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/plugins`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 403, 404, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/listPluginWidgets */
const listPluginWidgets = (...params) => async (orgCanonical, placement) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({ placement }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/plugin_widgets`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 403, 404, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** GET /organizations/{org}/nav — NO_REDIRECT: 404 means no config saved yet, falls through to empty default */
const getOrgNav = (...params) => async (orgCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/nav`,
    null,
    statusFromHandlers(handlers, 403, 404, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** PUT /organizations/{org}/nav */
const putOrgNav = (...params) => async (orgCanonical, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/nav`,
    { body },
    statusFromHandlers(handlers, 403, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/listPluginWidgetViews */
const listPluginWidgetViews = (...params) => async (orgCanonical, pluginInstallId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/plugins/${pluginInstallId}/widget-views`,
    null,
    statusFromHandlers(handlers, 403, 404),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updatePluginWidgetView */
const updatePluginWidgetView = (...params) => async (orgCanonical, widgetViewId, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/plugins/widget-views/${widgetViewId}`,
    { body },
    statusFromHandlers(handlers, 403, 404, 409, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getPluginWidgetData */
const getPluginWidgetData = (...params) => async (orgCanonical, widgetId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/plugin_widgets/${widgetId}/query`,
    null,
    statusFromHandlers(handlers, 403, 404, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/listComponentPlugins */
const listComponentPlugins = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/plugins`,
    null,
    statusFromHandlers(handlers, 403, 404, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updateComponentPluginRelation */
const updateComponentPluginRelation = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, pluginId, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/plugins/${pluginId}/relation`,
    { body },
    statusFromHandlers(handlers, 403, 404, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/listComponentPluginWidgets */
const listComponentPluginWidgets = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/plugin_widgets`,
    null,
    statusFromHandlers(handlers, 403, 404, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getComponentPluginWidgetData */
const getComponentPluginWidgetData = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, widgetId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/plugin_widgets/${widgetId}/query`,
    null,
    statusFromHandlers(handlers, 403, 404, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

// Preflight for the widget iframe: an <iframe> can't surface the proxy's 4xx
// "plugin not running" (it fires `load`, not `error`, and is cross-origin), so
// the caller probes here and branches on `res.ok`. Registered with
// `NO_ERROR_HANDLING` so 401/403/404/422 fall through as a non-ok response
// instead of triggering a global redirect or alert.
/** https://docs.staging.cycloid.io/api/index.html#operation/getPluginWidgetIframeProxy */
const getPluginWidgetIframeProxy = (...params) => async (orgCanonical, widgetId, jwt) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/plugin_widgets/${widgetId}/${jwt}/iframe/`,
    null,
    statusFromHandlers(handlers, 401, 403, 404, 422),
  )
  return applyHandlers(res, handlers)
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getComponentPluginWidgetIframeProxy */
const getComponentPluginWidgetIframeProxy = (...params) => async (orgCanonical, projectCanonical, envCanonical, componentCanonical, widgetId, jwt) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/plugin_widgets/${widgetId}/${jwt}/iframe/`,
    null,
    statusFromHandlers(handlers, 401, 403, 404, 422),
  )
  return applyHandlers(res, handlers)
}

// VERSIONS

/** https://docs.staging.cycloid.io/api/index.html#operation/createPluginRegistryPluginVersion */
const createPluginRegistryPluginVersion = (...params) => async (orgCanonical, pluginRegistryId, pluginId, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/plugin_registries/${pluginRegistryId}/plugins/${pluginId}/versions`,
    { body },
    statusFromHandlers(handlers, 411, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/listPluginVersions */
const listPluginVersions = (...params) => async (orgCanonical, pluginRegistryId, pluginId, pagination) => {
  const [authRequest, ...handlers] = params
  const query = _.pickBy({ ...pagination }, (item) => !_.$isEmpty(item))
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/plugin_registries/${pluginRegistryId}/plugins/${pluginId}/versions`,
    _.$isEmpty(query) ? null : { query },
    statusFromHandlers(handlers, 403, 404, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getPluginRegistryPluginVersion */
const getPluginRegistryPluginVersion = (...params) => async (orgCanonical, pluginRegistryId, pluginId, pluginVersionId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/plugin_registries/${pluginRegistryId}/plugins/${pluginId}/versions/${pluginVersionId}`,
    null,
    statusFromHandlers(handlers, 403, 404),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deletePluginRegistryPluginVersion */
const deletePluginRegistryPluginVersion = (...params) => async (orgCanonical, pluginRegistryId, pluginId, pluginVersionId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}/plugin_registries/${pluginRegistryId}/plugins/${pluginId}/versions/${pluginVersionId}`,
    null,
    statusFromHandlers(handlers, 403, 404),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/retryPluginRegistryPluginVersion */
const retryPluginRegistryPluginVersion = (...params) => async (orgCanonical, pluginRegistryId, pluginId, pluginVersionId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/plugin_registries/${pluginRegistryId}/plugins/${pluginId}/versions/${pluginVersionId}/retry`,
    null,
    statusFromHandlers(handlers, 403, 404),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/createPluginInstall */
const createPluginInstall = (...params) => async (orgCanonical, pluginRegistryId, pluginId, pluginVersionId, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/plugin_registries/${pluginRegistryId}/plugins/${pluginId}/versions/${pluginVersionId}/install`,
    { body },
    statusFromHandlers(handlers, 411, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updatePluginInstall */
const updatePluginInstall = (...params) => async (orgCanonical, installId, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/plugins/${installId}`,
    { body },
    statusFromHandlers(handlers, 403, 404, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/deletePluginInstall */
const deletePluginInstall = (...params) => async (orgCanonical, installId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    DELETE,
    `/organizations/${orgCanonical}/plugins/${installId}`,
    null,
    statusFromHandlers(handlers, 403, 404),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

// The success answer is a 202 with no body, so `jsonBodyParser` alone returns null
// for both "resync accepted" and "request never made it" (a network failure or a 5xx
// makes the client alert and return null). Reporting the outcome separately from the
// body lets the caller stay quiet when the client has already alerted, instead of
// following that error with a success one.
/** https://docs.staging.cycloid.io/api/index.html#operation/resyncPluginInstall */
const resyncPluginInstall = (...params) => async (orgCanonical, pluginInstallId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    POST,
    `/organizations/${orgCanonical}/plugins/${pluginInstallId}/resync`,
    null,
    statusFromHandlers(handlers, 403, 404, 422),
  )
  const handledRes = await applyHandlers(res, handlers)
  if (!handledRes) return null
  return await jsonBodyParser(handledRes) ?? {}
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getPluginBySlug */
const getPluginBySlug = (...params) => async (orgCanonical, pluginRegistryId, pluginInstallSlug) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/plugin_registries/${pluginRegistryId}/plugins/by-slug/${pluginInstallSlug}`,
    null,
    statusFromHandlers(handlers, 403, 404),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/getPluginInstallSharing */
const getPluginInstallSharing = (...params) => async (orgCanonical, pluginInstallId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/plugins/${pluginInstallId}/sharing`,
    null,
    statusFromHandlers(handlers, 403, 404),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/updatePluginInstallSharing */
const updatePluginInstallSharing = (...params) => async (orgCanonical, pluginInstallId, body) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    PUT,
    `/organizations/${orgCanonical}/plugins/${pluginInstallId}/sharing`,
    { body },
    statusFromHandlers(handlers, 403, 404, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/listPluginLogs */
const listPluginLogs = (...params) => async (orgCanonical, pluginInstallId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/plugins/${pluginInstallId}/logs`,
    null,
    statusFromHandlers(handlers, 403, 404, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** https://docs.staging.cycloid.io/api/index.html#operation/listPluginRegistryPluginVersionLogs */
const listPluginRegistryPluginVersionLogs = (...params) => async (orgCanonical, pluginRegistryId, pluginId, pluginVersionId) => {
  const [authRequest, ...handlers] = params
  const res = await authRequest(
    GET,
    `/organizations/${orgCanonical}/plugin_registries/${pluginRegistryId}/plugins/${pluginId}/versions/${pluginVersionId}/logs`,
    null,
    statusFromHandlers(handlers, 403, 404, 422),
  )
  return jsonBodyParser(await applyHandlers(res, handlers))
}

/** Create an object which has methods to perform any of the YD API Spec operations.
 * The methods are named as the operation ID field used in the Swagger Spec.
 * The object may contain other methods to do API calls combined together.
 *
 * @param {httpClient} httpClient - Instance created by
 *      youdeploy-api-http-client~createHTTPClient
 * @param {Object} i18n - Instance of vue-i18n
 * @param {Object} router - Instance of vue-router
 * @returns An object which contains the methods mentioned above.
 *              Each method receive the path, query and/or body which the API
 *              operation requires.
 *
 * @see [youdeploy-api-http-client]{@link ./youdeploy-api-http-client.js}
 *
 */
export default function createAPI (httpClient, _i18n, router) {
  const handle500UnexpectedError = makeHandler500UnexpectedError()
  const handle401Unauthenticated = makeHandler401Unauthenticated(router)
  const handle404NotFound = makeHandler404NotFound(router)
  const handle403Forbidden = makeHandler403Forbidden(router)
  const handle403ShowForbidden = makeHandler403ShowForbidden(router)
  const handle409Conflict = makeHandler409Conflict()

  const { publicRequest } = httpClient
  const authRequest = authReqWithAutoRefreshToken(httpClient.authRequest, router)

  const base = [authRequest, handle401Unauthenticated, handle403Forbidden]
  // Same as `base`, but the denial is handed back to the caller instead of being swallowed
  const showForbiddenBase = [authRequest, handle401Unauthenticated, handle403ShowForbidden]
  const params = {
    /**
      * For non GET requests
      * @param authRequest
      * @param handle401Unauthenticated
      * @param handle403Forbidden
      * @param handle404NotFound
      */
    default: [...base, handle404NotFound],

    /**
       * For GET requests
       * - extends base params, adds handlers for 404 responses
       * @param authRequest
       * @param handle401Unauthenticated
       * @param handle403Forbidden
       * @param handle404NotFound
       */
    GET: [...base, handle404NotFound],

    /**
       * For requests where we want to handle the error response manually (no 404 page)
       * - extends base params
       * @param authRequest
       * @param handle401Unauthenticated
       * @param handle403Forbidden
       */
    NO_REDIRECT: [...base],

    /**
       * For requests where the caller inspects the raw response itself (res.ok) and
       * handles every non-2xx in place — no global redirects (401/403/404) and no alerts.
       * @param authRequest
       */
    NO_ERROR_HANDLING: [authRequest],

    /**
       * For requests where we don't want to show the global error for 500 responses
       * - does not extend base params, as handle401Unauthenticated redirects us to login page
       * - adds handler for 500 responses
       * @param authRequest
       * @param handle500UnexpectedError
       */
    NO_UNEXPECTED_ERROR: [authRequest, handle500UnexpectedError],

    /**
       * For requests where we need to show conflicts (errors) to the user, that stop the action succeeding.
       * - adds handler for 409 responses
       * @param authRequest
       * @param handle401Unauthenticated
       * @param handle403Forbidden
       * @param handle409Conflict
       */
    SHOW_CONFLICTS: [...base, handle409Conflict],

    /**
       * For requests whose denial the caller has to surface itself.
       * - swaps handle403Forbidden, which swallows the 403 of a request made from a page the user
       *   is allowed to view, for the variant that returns it as an inline error
       * @param authRequest
       * @param handle401Unauthenticated
       * @param handle403ShowForbidden
       */
    SHOW_FORBIDDEN: [...showForbiddenBase],

    /**
       * SHOW_FORBIDDEN for the non-GET requests, which keep the 404 page of the `default` params
       * @param authRequest
       * @param handle401Unauthenticated
       * @param handle403ShowForbidden
       * @param handle404NotFound
       */
    SHOW_FORBIDDEN_NOT_FOUND: [...showForbiddenBase, handle404NotFound],

    /**
       * Public requests: usually for use in pages where User is not signed in yet
       * @param publicRequest
       */
    public: [publicRequest],
  }

  return {
    // APP
    getAppConfig: getAppConfig(...params.GET),
    getOrgConfig: getOrgConfig(...params.GET),
    getAppVersion: getAppVersion(...params.GET),
    getCountries: getCountries(...params.GET),
    getStatus: getStatus(...params.GET),
    getSentryEnable: getSentryEnable(...params.GET),

    // AUTH, LOGIN & USER
    createOAuthUser: createOAuthUser(...params.public),
    deleteUserAccount: deleteUserAccount(...params.NO_UNEXPECTED_ERROR),
    getOAuthUser: getOAuthUser(...params.public),
    getUserAccount: getUserAccount(...params.GET),
    login: login(...params.public),
    emailAuthenticationVerification: emailAuthenticationVerification(...params.public),
    passwordResetReq: passwordResetReq(...params.public),
    passwordResetUpdate: passwordResetUpdate(...params.public),
    refreshToken: refreshToken(...params.GET),
    resendVerifyEmail: emailVerificationResend(...params.default),
    signUp: signUp(...params.public),
    signUpAWSMarketplace: signUpAWSMarketplace(...params.public),
    updateUserAccount: updateUserAccount(...params.default),
    updateUserGuide: updateUserGuide(...params.default),
    verifyEmail: emailVerification(...params.NO_REDIRECT),
    listNotifications: listNotifications(...params.GET),
    deleteNotification: deleteNotification(...params.default),
    deleteAllNotifications: deleteAllNotifications(...params.default),
    markNotificationAsRead: markNotificationAsRead(...params.default),
    markNotificationAsUnread: markNotificationAsUnread(...params.default),
    markAllNotificationsAsRead: markAllNotificationsAsRead(...params.default),
    listWatchRules: listWatchRules(...params.GET),
    getWatchRule: getWatchRule(...params.GET),
    updateWatchRule: updateWatchRule(...params.default),
    deleteWatchRule: deleteWatchRule(...params.default),
    createWatchRule: createWatchRule(...params.default),
    getNotificationsSettings: getNotificationsSettings(...params.GET),
    updateNotificationsSettings: updateNotificationsSettings(...params.default),
    getEventsActionsAndEntities: getEventsActionsAndEntities(...params.default),

    // ORGANIZATION
    createOrg: createOrg(...params.default),
    createOrgChild: createOrgChild(...params.default),
    deleteOrg: deleteOrg(...params.SHOW_CONFLICTS),
    getOrg: getOrg(...params.GET),
    getOrgs: getOrgs(...params.GET),
    getOrgSummary: getOrgSummary(...params.NO_REDIRECT),
    updateOrg: updateOrg(...params.default),
    listAuthentications: listAuthentications(...params.GET),
    getAuthentication: getAuthentication(...params.GET),
    updateAuthentication: updateAuthentication(...params.default),
    updateOrganizationCreationNotAllowed: updateOrganizationCreationNotAllowed(...params.default),
    /** 404 when no custom message exists — must not redirect to global not-found page */
    getOrganizationCreationNotAllowed: getOrganizationCreationNotAllowed(...params.NO_REDIRECT),

    // ORGANIZATION------OIDC GROUP MAPPINGS
    listOIDCGroupMappings: listOIDCGroupMappings(...params.NO_REDIRECT),
    createOIDCGroupMapping: createOIDCGroupMapping(...params.NO_REDIRECT),
    deleteOIDCGroupMapping: deleteOIDCGroupMapping(...params.NO_REDIRECT),
    getOIDCOrganizationSettings: getOIDCOrganizationSettings(...params.NO_REDIRECT),
    updateOIDCOrganizationSettings: updateOIDCOrganizationSettings(...params.NO_REDIRECT),

    // ORGANIZATION------APPEARANCE
    createAppearance: createAppearance(...params.default),
    deleteAppearance: deleteAppearance(...params.default),
    getActiveAppearance: getActiveAppearance(...params.GET),
    getAppearance: getAppearance(...params.GET),
    getDefaultAppearance: getDefaultAppearance(...params.GET),
    listAppearances: listAppearances(...params.GET),
    updateAppearance: updateAppearance(...params.default),

    // ORGANIZATION------API KEY
    createAPIKey: createAPIKey(...params.default),
    deleteAPIKey: deleteAPIKey(...params.default),
    getAPIKey: getAPIKey(...params.GET),
    getAPIKeys: getAPIKeys(...params.GET),
    updateAPIKey: updateAPIKey(...params.default),

    // ORGANIZATION------ANCESTORS
    getOrgAncestors: getOrgAncestors(...params.GET),

    // ORGANIZATION------CLOUD PROVIDERS
    getCloudProviders: getCloudProviders(...params.GET),

    // ORGANIZATION------CHILDREN
    getOrgChildren: getOrgChildren(...params.default),

    // ORGANIZATION------CATALOG REPOSITORY
    createServiceCatalogSource: createServiceCatalogSource(...params.default),
    deleteServiceCatalogSource: deleteServiceCatalogSource(...params.default),
    getRepoBranches: getRepoBranches(...params.NO_REDIRECT),
    getServiceCatalogSource: getServiceCatalogSource(...params.GET),
    listCatalogRepositoryVersions: listCatalogRepositoryVersions(...params.GET),
    getServiceCatalogSources: getServiceCatalogSources(...params.GET),
    /** 404 (branch removed) must surface as an inline error, not a global not-found redirect */
    refreshServiceCatalogSource: refreshServiceCatalogSource(...params.NO_REDIRECT),
    refreshServiceCatalogSourceVersions: refreshServiceCatalogSourceVersions(...params.default),
    /** 404 (branch removed) must surface as an inline error, not a global not-found redirect */
    updateServiceCatalogSource: updateServiceCatalogSource(...params.NO_REDIRECT),
    validateServiceCatalogSource: validateServiceCatalogSource(...params.NO_REDIRECT),

    // ORGANIZATION------CONFIG REPOSITORY
    createConfigRepository: createConfigRepository(...params.default),
    deleteConfigRepository: deleteConfigRepository(...params.default),
    getConfigRepositories: getConfigRepositories(...params.GET),
    getConfigRepository: getConfigRepository(...params.GET),
    updateConfigRepository: updateConfigRepository(...params.default),

    // ORGANIZATION------PLUGIN REGISTRY
    createPluginManager: createPluginManager(...params.default),
    listPluginManagers: listPluginManagers(...params.GET),
    getPluginManager: getPluginManager(...params.GET),
    deletePluginManager: deletePluginManager(...params.default),
    updatePluginManager: updatePluginManager(...params.default),
    createPluginRegistry: createPluginRegistry(...params.default),
    deletePluginRegistry: deletePluginRegistry(...params.default),
    getPluginRegistries: getPluginRegistries(...params.GET),
    updatePluginRegistry: updatePluginRegistry(...params.default),

    // ORGANIZATION------PLUGINS
    createPluginRegistryPlugin: createPluginRegistryPlugin(...params.default),
    getPluginRegistryPlugin: getPluginRegistryPlugin(...params.GET),
    deletePluginRegistryPlugin: deletePluginRegistryPlugin(...params.default),
    updatePluginRegistryPlugin: updatePluginRegistryPlugin(...params.default),
    listPlugins: listPlugins(...params.GET),
    listPluginWidgets: listPluginWidgets(...params.GET),
    listPluginWidgetViews: listPluginWidgetViews(...params.GET),
    updatePluginWidgetView: updatePluginWidgetView(...params.default),
    getPluginWidgetData: getPluginWidgetData(...params.GET),
    getPluginWidgetIframeProxy: getPluginWidgetIframeProxy(...params.NO_ERROR_HANDLING),
    getOrgNav: getOrgNav(...params.NO_REDIRECT),
    putOrgNav: putOrgNav(...params.default),

    // ORGANIZATION------PLUGIN VERSIONS
    createPluginRegistryPluginVersion: createPluginRegistryPluginVersion(...params.default),
    listPluginVersions: listPluginVersions(...params.GET),
    getPluginRegistryPluginVersion: getPluginRegistryPluginVersion(...params.GET),
    deletePluginRegistryPluginVersion: deletePluginRegistryPluginVersion(...params.default),
    retryPluginRegistryPluginVersion: retryPluginRegistryPluginVersion(...params.default),
    listPluginRegistryPluginVersionLogs: listPluginRegistryPluginVersionLogs(...params.GET),
    listPluginLogs: listPluginLogs(...params.GET),
    createPluginInstall: createPluginInstall(...params.default),
    updatePluginInstall: updatePluginInstall(...params.default),
    deletePluginInstall: deletePluginInstall(...params.default),
    resyncPluginInstall: resyncPluginInstall(...params.default),
    getPluginBySlug: getPluginBySlug(...params.GET),
    getPluginInstallSharing: getPluginInstallSharing(...params.GET),
    updatePluginInstallSharing: updatePluginInstallSharing(...params.default),

    // ORGANIZATION------COST
    costEstimateForms: costEstimateForms(...params.NO_REDIRECT),

    // ORGANIZATION------CLOUD COST MANAGEMENT
    getCloudCostManagementTagMapping: getCloudCostManagementTagMapping(...params.GET),
    updateCloudCostManagementTagMapping: updateCloudCostManagementTagMapping(...params.default),
    getCloudCostManagementProvider: getCloudCostManagementProvider(...params.GET),
    getCloudCostManagementDashboard: getCloudCostManagementDashboard(...params.GET),
    pauseCloudAccountFinops: pauseCloudAccountFinops(...params.default),
    unpauseCloudAccountFinops: unpauseCloudAccountFinops(...params.default),
    ingestCloudAccountFinops: ingestCloudAccountFinops(...params.default),

    // ORGANIZATION------CREDENTIAL
    createCredential: createCredential(...params.default),
    deleteCredential: deleteCredential(...params.default),
    getCredential: getCredential(...params.GET),
    // For callers to which a missing credential is an expected answer rather than a dead
    // link: params.GET would send the 404 to the global notFound redirect instead.
    getCredentialIfExists: getCredential(...params.NO_REDIRECT),
    listCredentials: listCredentials(...params.GET),
    getCredentialOptions: getCredentialOptions(...params.GET),
    getCredentialOptionsBare: getCredentialOptionsBare(...params.default),
    getCycloidCredential: getCycloidCredential(...params.NO_REDIRECT),
    updateCredential: updateCredential(...params.default),

    // ORGANIZATION------EVENTS
    getOrgEvents: getOrgEvents(...params.GET),
    getOrgEventsTags: getOrgEventsTags(...params.GET),

    // ORGANIZATION------EXTERNAL BACKEND
    createExternalBackend: createExternalBackend(...params.default),
    deleteExternalBackend: deleteExternalBackend(...params.default),
    getExternalBackend: getExternalBackend(...params.default),
    getExternalBackends: getExternalBackends(...params.NO_REDIRECT),
    updateExternalBackend: updateExternalBackend(...params.default),

    // ORGANIZATION------INFRAPOLICY
    createInfraPolicy: createInfraPolicy(...params.default),
    deleteInfraPolicy: deleteInfraPolicy(...params.default),
    getInfraPolicies: getInfraPolicies(...params.GET),
    getInfraPolicy: getInfraPolicy(...params.GET),
    updateInfraPolicy: updateInfraPolicy(...params.default),

    // ORGANIZATION------INVENTORY
    getInventoryResources: getInventoryResources(...params.GET),

    // ORGANIZATION------INVITATION
    deleteInvitation: deleteInvitation(...params.default),
    getInvitations: getInvitations(...params.GET),
    getPendingInvitation: getPendingInvitation(...params.GET),
    resendInvitation: resendInvitation(...params.default),

    // ORGANIZATION------KPI
    getKPI: getKPI(...params.GET),
    getKPIs: getKPIs(...params.GET),
    createKPI: createKPI(...params.NO_REDIRECT),
    createKPIFavorite: createKPIFavorite(...params.default),
    updateKPI: updateKPI(...params.default),
    deleteKPI: deleteKPI(...params.default),
    deleteKPIFavorite: deleteKPIFavorite(...params.default),

    // ORGANIZATION------LICENCE & SUBSCRIPTION
    activateLicence: activateLicence(...params.default),
    createSubscription: createSubscription(...params.default),
    getLicence: getLicence(...params.GET),

    // ORGANIZATION------MEMBER
    getOrgMember: getOrgMember(...params.GET),
    getOrgMembers: getOrgMembers(...params.GET),
    inviteUserToOrgMember: inviteUserToOrgMember(...params.NO_REDIRECT),
    removeOrgMember: removeOrgMember(...params.default),
    updateOrgMember: updateOrgMember(...params.default),

    // ORGANIZATION------PERMISSIONS
    getPolicies: getPolicies(...params.GET),
    canDo: canDo(...params.NO_UNEXPECTED_ERROR),
    canDoBatch: canDoBatch(...params.NO_UNEXPECTED_ERROR),

    // ORGANIZATION------PROJECT
    createProject: createProject(...params.NO_REDIRECT),
    createProjectFavorite: createProjectFavorite(...params.default),
    deleteProject: deleteProject(...params.default),
    deleteProjectFavorite: deleteProjectFavorite(...params.default),
    getProject: getProject(...params.GET),
    getProjects: getProjects(...params.GET),
    updateProject: updateProject(...params.NO_REDIRECT),

    // ORGANIZATION------PROJECT------ENVIRONMENT
    createProjectEnvironment: createProjectEnvironment(...params.default),
    linkEnvironmentToProject: linkEnvironmentToProject(...params.default),
    deleteProjectEnvironment: deleteProjectEnvironment(...params.default),
    getProjectEnvironments: getProjectEnvironments(...params.GET),
    getProjectEnvironmentConfig: getProjectEnvironmentConfig(...params.GET),
    getProjEnvLogEntries: getProjEnvLogEntries(...params.GET),
    getProjEnvLogSources: getProjEnvLogSources(...params.NO_REDIRECT),
    getProjInfrastructure: getProjInfrastructure(...params.NO_REDIRECT),
    getEnvironments: getEnvironments(...params.GET),
    getOrganizationEnvironment: getOrganizationEnvironment(...params.GET),
    getOrganizationNamingRules: getOrganizationNamingRules(...params.NO_REDIRECT),
    createOrganizationEnvironment: createOrganizationEnvironment(...params.default),
    updateOrganizationEnvironment: updateOrganizationEnvironment(...params.default),
    deleteOrganizationEnvironment: deleteOrganizationEnvironment(...params.default),
    updateOrganizationNamingRules: updateOrganizationNamingRules(...params.default),
    deleteOrganizationNamingRules: deleteOrganizationNamingRules(...params.default),
    getEnvironmentTypes: getEnvironmentTypes(...params.GET),
    createEnvironmentType: createEnvironmentType(...params.default),
    updateEnvironmentType: updateEnvironmentType(...params.default),
    deleteEnvironmentType: deleteEnvironmentType(...params.default),
    setEnvironmentTypeLabelSelector: setEnvironmentTypeLabelSelector(...params.default),
    deleteEnvironmentTypeLabelSelector: deleteEnvironmentTypeLabelSelector(...params.default),
    // SHOW_FORBIDDEN_NOT_FOUND: the component writes are authorized on the component permissions
    // now (BE-473), so they can be denied on pages the user may otherwise view. handle403Forbidden
    // swallowed that into a null body -- read here as an empty error list, i.e. a success -- and
    // would bounce to the forbidden page from a route that does require the action. The denial is
    // returned instead, so it surfaces through each action's own alert. The 404 page is kept.
    createEnvComponent: createEnvComponent(...params.SHOW_FORBIDDEN_NOT_FOUND),
    deleteEnvComponent: deleteEnvComponent(...params.SHOW_FORBIDDEN_NOT_FOUND),
    updateEnvComponent: updateEnvComponent(...params.SHOW_FORBIDDEN_NOT_FOUND),
    // SHOW_FORBIDDEN: handle403Forbidden swallowed the denial into a null body, which the store
    // then read as an empty error list and announced as a successful configure. The denial is
    // returned so it surfaces as an error alert instead (BE-473).
    configureEnvComponent: configureEnvComponent(...params.SHOW_FORBIDDEN_NOT_FOUND),
    // SHOW_FORBIDDEN: reading a component's current configuration also reaches the project's
    // config repository, so a role without it is denied here. The tab has to say so in an alert
    // rather than render an empty form as if the component had no configuration (BE-473). A 404
    // (version branch removed) stays an inline error too, never a global not-found redirect.
    getComponentConfig: getComponentConfig(...params.SHOW_FORBIDDEN),

    // ORGANIZATION------COMPONENT
    getComponents: getComponents(...params.GET),
    listComponentPlugins: listComponentPlugins(...params.GET),
    updateComponentPluginRelation: updateComponentPluginRelation(...params.default),
    listComponentPluginWidgets: listComponentPluginWidgets(...params.GET),
    getComponentPluginWidgetData: getComponentPluginWidgetData(...params.GET),
    getComponentPluginWidgetIframeProxy: getComponentPluginWidgetIframeProxy(...params.NO_ERROR_HANDLING),

    // ORGANIZATION------CLOUD ACCOUNTS
    getCloudAccounts: getCloudAccounts(...params.GET),
    getCloudAccount: getCloudAccount(...params.GET),
    createCloudAccount: createCloudAccount(...params.default),
    updateCloudAccount: updateCloudAccount(...params.default),
    updateCloudAccountFinops: updateCloudAccountFinops(...params.default),
    deleteCloudAccount: deleteCloudAccount(...params.default),

    // ORGANIZATION------ENVIRONMENT------COMPONENT
    // SHOW_FORBIDDEN_NOT_FOUND: the environment page stays viewable without the component list
    // permission, so the list's own denial has to reach the fetcher to be shown as a notice in
    // place of the list, rather than being swallowed into an empty list that reads as "no
    // components" (BE-473). SHOW_FORBIDDEN_NOT_FOUND (not SHOW_FORBIDDEN) keeps the 404 handler
    // so a missing project/env still renders the not-found page instead of an empty list.
    getEnvironmentComponents: getEnvironmentComponents(...params.SHOW_FORBIDDEN_NOT_FOUND),
    // SHOW_FORBIDDEN (no 404 redirect): the debounced search and pagination refetches must not
    // bounce the user off the page on a later 404 — a miss on a refetch is surfaced as the same
    // forbidden notice instead. Only the initial mount fetch uses the redirecting variant above.
    getEnvironmentComponentsNoRedirect: getEnvironmentComponents(...params.SHOW_FORBIDDEN),
    // Best-effort (sync-status poll): no 404 redirect, no error alert.
    getComponent: getComponent(...params.NO_ERROR_HANDLING),

    // ORGANIZATION------PROJECT------KPIS
    getProjectKPIs: getProjectKPIs(...params.GET),

    // ORGANIZATION------PROJECT------PIPELINE
    createPipeline: createPipeline(...params.default),
    deletePipeline: deletePipeline(...params.default),
    diffPipeline: diffPipeline(...params.default),
    getPipeline: getPipeline(...params.GET),
    getPipelineSilent: getPipelineSilent(...params.NO_REDIRECT),
    getPipelineConfig: getPipelineConfig(...params.GET),
    getPipelineResources: getPipelineResources(...params.GET),
    getPipelines: getPipelines(...params.NO_REDIRECT),
    getPipelineVariables: getPipelineVariables(...params.NO_REDIRECT),
    getProjectPipelines: getProjectPipelines(...params.GET),
    syncedPipeline: syncedPipeline(...params.GET),
    pausePipeline: pausePipeline(...params.default),
    unpausePipeline: unpausePipeline(...params.default),
    updatePipeline: updatePipeline(...params.default),

    // ORGANIZATION------PROJECT------PIPELINE------BUILD
    abortBuild: abortBuild(...params.default),
    createBuild: createBuild(...params.default),
    getBuild: getBuild(...params.GET),
    getBuildPlan: getBuildPlan(...params.GET),
    getBuildPreparation: getBuildPreparation(...params.GET),
    getBuildResources: getBuildResources(...params.GET),
    getBuilds: getBuilds(...params.GET),
    getBuildsWithVersionAsInput: getBuildsWithVersionAsInput(...params.GET),
    getBuildsWithVersionAsOutput: getBuildsWithVersionAsOutput(...params.GET),
    rerunBuild: rerunBuild(...params.default),

    // ORGANIZATION------PROJECT------PIPELINE------JOB
    clearTaskCache: clearTaskCache(...params.default),
    getJob: getJob(...params.GET),
    getJobs: getJobs(...params.GET),
    pauseJob: pauseJob(...params.default),
    unpauseJob: unpauseJob(...params.default),

    // ORGANIZATION------PROJECT------PIPELINE------RESOURCE
    checkResource: checkResource(...params.default),
    disableResourceVersion: disableResourceVersion(...params.default),
    enableResourceVersion: enableResourceVersion(...params.default),
    getPipelineResource: getPipelineResource(...params.GET),
    getResourceVersions: getResourceVersions(...params.GET),
    pinResourceVersion: pinResourceVersion(...params.default),
    resourceSetPinComment: resourceSetPinComment(...params.default),
    unpinResource: unpinResource(...params.default),
    getTerraformOutputs: getTerraformOutputs(...params.GET),
    pinTerraformOutput: pinTerraformOutput(...params.default),
    unpinTerraformOutput: unpinTerraformOutput(...params.default),

    // ORGANIZATION------PROJECT------STACK FORMS
    createFormsConfig: createFormsConfig(...params.default),
    validateFormsFile: validateFormsFile(...params.default),

    // ORGANIZATION------RESOURCE
    getResource: getResource(...params.GET),

    // ORGANIZATION------ROLE
    createOrgRole: createOrgRole(...params.default),
    deleteOrgRole: deleteOrgRole(...params.default),
    getOrgRole: getOrgRole(...params.GET),
    getOrgRoles: getOrgRoles(...params.GET),
    updateOrgRole: updateOrgRole(...params.default),

    // ORGANIZATION------RUNNING INFRA
    getOrgRunningInfraAWS: getOrgRunningInfraAWS(...params.GET),
    getOrgRunningInfraAWSInstances: getOrgRunningInfraAWSInstances(...params.GET),

    // ORGANIZATION------STACK
    createServiceCatalog: createServiceCatalog(...params.default),
    createServiceCatalogFromTemplate: createServiceCatalogFromTemplate(...params.default),
    deleteServiceCatalog: deleteServiceCatalog(...params.SHOW_CONFLICTS),
    getFormsValuesRef: getFormsValuesRef(...params.default),
    getServiceCatalog: getServiceCatalog(...params.GET),
    getServiceCatalogTemplateUseCases: getServiceCatalogTemplateUseCases(...params.GET),
    getServiceCatalogVersions: getServiceCatalogVersions(...params.GET),
    getStackVersionAdoption: getStackVersionAdoption(...params.GET),
    getComponentStackConfiguration: getComponentStackConfiguration(...params.SHOW_FORBIDDEN),
    getServiceCatalogs: getServiceCatalogs(...params.GET),
    updateServiceCatalog: updateServiceCatalog(...params.default),
    updateServiceCatalogTerraform: updateServiceCatalogTerraform(...params.default),
    validateServiceCatalogDependencies: validateServiceCatalogDependencies(...params.GET),

    // ORGANIZATION------TEAM
    createTeam: createTeam(...params.default),
    deleteTeam: deleteTeam(...params.default),
    getTeam: getTeam(...params.GET),
    getTeams: getTeams(...params.SHOW_FORBIDDEN_NOT_FOUND),
    updateTeam: updateTeam(...params.default),

    // ORGANIZATION------TEAM------MEMBER
    assignMemberToTeam: assignMemberToTeam(...params.default),
    getTeamMember: getTeamMember(...params.GET),
    getTeamMembers: getTeamMembers(...params.SHOW_FORBIDDEN_NOT_FOUND),
    unassignMemberFromTeam: unassignMemberFromTeam(...params.default),

    // ORGANIZATION------TERRAFORM
    getServiceCatalogTerraform: getServiceCatalogTerraform(...params.GET),
    getServiceCatalogTerraformDiagram: getServiceCatalogTerraformDiagram(...params.GET),
    getTerraformProvider: getTerraformProvider(...params.GET),
    getTerraformProviderDataSource: getTerraformProviderDataSource(...params.GET),
    getTerraformProviderDataSources: getTerraformProviderDataSources(...params.GET),
    getTerraformProviderResource: getTerraformProviderResource(...params.GET),
    getTerraformProviderResources: getTerraformProviderResources(...params.GET),
    getTerraformProviders: getTerraformProviders(...params.GET),
    terraformJSONToHCL: terraformJSONToHCL(...params.default),
    terraformValidate: terraformValidate(...params.default),
    updateServiceCatalogTerraformDiagram: updateServiceCatalogTerraformDiagram(...params.default),

    // ORGANIZATION------WORKERS
    getWorkers: getWorkers(...params.GET),
  }
}
